/**
 * Approvals Page
 *
 * Displays pending approval requests and allows approve/reject actions.
 */

import { useEffect, useState, type CSSProperties } from 'react';
import { tokens } from '../local-components';
import { useAgent, type ApprovalListQuery } from '../hooks/useAgent';
import { useDashboardStore, type ApprovalRequest } from '../stores/dashboard';

// ═══════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════

const pageStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing[6],
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const titleStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes['2xl'],
  fontWeight: tokens.fontWeights.bold,
  color: tokens.colors.textPrimary,
  margin: 0,
};

const filterContainerStyle: CSSProperties = {
  display: 'flex',
  gap: tokens.spacing[2],
};

const filterButtonStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  fontWeight: tokens.fontWeights.medium,
  color: tokens.colors.textMuted,
  backgroundColor: 'transparent',
  border: `1px solid ${tokens.colors.border}`,
  borderRadius: tokens.radii.md,
  padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
  cursor: 'pointer',
};

const filterButtonActiveStyle: CSSProperties = {
  ...filterButtonStyle,
  color: tokens.colors.void,
  backgroundColor: tokens.colors.mint,
  borderColor: tokens.colors.mint,
};

const cardStyle: CSSProperties = {
  backgroundColor: tokens.colors.surface,
  borderRadius: tokens.radii.lg,
  padding: tokens.spacing[5],
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing[4],
};

const cardHeaderStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
};

const cardTitleStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.lg,
  fontWeight: tokens.fontWeights.semibold,
  color: tokens.colors.textPrimary,
  margin: 0,
};

const cardVersionStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.textMuted,
  marginLeft: tokens.spacing[2],
};

const cardDetailsStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: tokens.spacing[3],
};

const detailItemStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing[1],
};

const detailLabelStyle: CSSProperties = {
  fontFamily: tokens.fonts.sans,
  fontSize: tokens.fontSizes.xs,
  fontWeight: tokens.fontWeights.medium,
  color: tokens.colors.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const detailValueStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.textPrimary,
};

const cardActionsStyle: CSSProperties = {
  display: 'flex',
  gap: tokens.spacing[3],
  marginTop: tokens.spacing[2],
  paddingTop: tokens.spacing[4],
  borderTop: `1px solid ${tokens.colors.border}`,
};

const approveButtonStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  fontWeight: tokens.fontWeights.medium,
  color: tokens.colors.void,
  backgroundColor: tokens.colors.mint,
  border: 'none',
  borderRadius: tokens.radii.md,
  padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
  cursor: 'pointer',
};

const rejectButtonStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  fontWeight: tokens.fontWeights.medium,
  color: tokens.colors.textPrimary,
  backgroundColor: 'transparent',
  border: `1px solid ${tokens.colors.danger}`,
  borderRadius: tokens.radii.md,
  padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
  cursor: 'pointer',
};

const buttonDisabledStyle: CSSProperties = {
  opacity: 0.5,
  cursor: 'not-allowed',
};

const loadingStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.textMuted,
  padding: tokens.spacing[6],
  textAlign: 'center',
};

const errorStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.danger,
  padding: tokens.spacing[4],
  backgroundColor: 'rgba(255, 71, 87, 0.1)',
  border: `1px solid ${tokens.colors.danger}`,
  borderRadius: tokens.radii.md,
};

const emptyStateStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.textMuted,
  padding: tokens.spacing[6],
  textAlign: 'center',
  backgroundColor: tokens.colors.surface,
  borderRadius: tokens.radii.lg,
};

const modalOverlayStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalContentStyle: CSSProperties = {
  backgroundColor: tokens.colors.surface,
  borderRadius: tokens.radii.lg,
  padding: tokens.spacing[6],
  width: '400px',
  maxWidth: '90vw',
};

const modalTitleStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.lg,
  fontWeight: tokens.fontWeights.semibold,
  color: tokens.colors.textPrimary,
  margin: 0,
  marginBottom: tokens.spacing[4],
};

const textareaStyle: CSSProperties = {
  width: '100%',
  minHeight: '100px',
  padding: tokens.spacing[3],
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.textPrimary,
  backgroundColor: tokens.colors.void,
  border: `1px solid ${tokens.colors.border}`,
  borderRadius: tokens.radii.md,
  resize: 'vertical',
  outline: 'none',
  boxSizing: 'border-box',
};

const modalActionsStyle: CSSProperties = {
  display: 'flex',
  gap: tokens.spacing[3],
  marginTop: tokens.spacing[4],
  justifyContent: 'flex-end',
};

const cancelButtonStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  fontWeight: tokens.fontWeights.medium,
  color: tokens.colors.textMuted,
  backgroundColor: 'transparent',
  border: `1px solid ${tokens.colors.border}`,
  borderRadius: tokens.radii.md,
  padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
  cursor: 'pointer',
};

// ═══════════════════════════════════════════════════════════════
// STATUS BADGE COMPONENT
// ═══════════════════════════════════════════════════════════════

function ApprovalStatusBadge({ status }: { status: ApprovalRequest['status'] }) {
  const getColor = (s: ApprovalRequest['status']): string => {
    switch (s) {
      case 'approved':
        return tokens.colors.success;
      case 'pending':
        return tokens.colors.warning;
      case 'rejected':
        return tokens.colors.danger;
      case 'expired':
        return tokens.colors.textMuted;
      case 'cancelled':
        return tokens.colors.textMuted;
    }
  };

  const badgeStyle: CSSProperties = {
    fontFamily: tokens.fonts.mono,
    fontSize: tokens.fontSizes.xs,
    fontWeight: tokens.fontWeights.semibold,
    color: getColor(status),
    backgroundColor: `${getColor(status)}20`,
    padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
    borderRadius: tokens.radii.sm,
    textTransform: 'uppercase',
  };

  return (
    <span style={badgeStyle} data-testid="approval-status-badge" data-status={status}>
      {status}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════
// REJECT MODAL COMPONENT
// ═══════════════════════════════════════════════════════════════

interface RejectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  artifactName: string;
}

function RejectModal({ isOpen, onClose, onConfirm, artifactName }: RejectModalProps) {
  const [reason, setReason] = useState('');

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (reason.trim() !== '') {
      onConfirm(reason.trim());
      setReason('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey && reason.trim() !== '') {
      handleConfirm();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div style={modalOverlayStyle} onClick={onClose} data-testid="reject-modal">
      <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={modalTitleStyle}>Reject {artifactName}</h3>
        <textarea
          style={textareaStyle}
          placeholder="Enter rejection reason (required)..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
          data-testid="reject-reason"
        />
        <div style={modalActionsStyle}>
          <button style={cancelButtonStyle} onClick={onClose} data-testid="reject-cancel">
            Cancel
          </button>
          <button
            style={{
              ...rejectButtonStyle,
              backgroundColor: tokens.colors.danger,
              color: tokens.colors.textPrimary,
              border: 'none',
              ...(reason.trim() === '' ? buttonDisabledStyle : {}),
            }}
            onClick={handleConfirm}
            disabled={reason.trim() === ''}
            data-testid="reject-confirm"
          >
            Reject
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// FILTER TYPES
// ═══════════════════════════════════════════════════════════════

type FilterStatus = ApprovalListQuery['status'] | 'all';

const filterOptions: { value: FilterStatus; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

export function Approvals() {
  const {
    isConnected,
    listApprovals,
    approveDeployment,
    rejectDeployment,
    approvals: storeApprovals,
  } = useAgent();
  const { setApprovals, removeApproval } = useDashboardStore();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterStatus>('pending');
  const [rejectModalState, setRejectModalState] = useState<{
    isOpen: boolean;
    requestId: string;
    artifactName: string;
  }>({
    isOpen: false,
    requestId: '',
    artifactName: '',
  });

  // Fetch approvals on mount and when filter changes
  useEffect(() => {
    if (!isConnected) return;

    const fetchApprovals = async () => {
      setIsLoading(true);
      try {
        const result = await listApprovals({
          status: filter === 'all' ? null : filter,
          environment: null,
          artifact: null,
          limit: null,
        });
        setApprovals(result);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setIsLoading(false);
      }
    };

    fetchApprovals();
  }, [isConnected, listApprovals, filter, setApprovals]);

  // Filter approvals from store based on current filter
  const approvals = storeApprovals.filter((a) => {
    if (filter === 'all') return true;
    return a.status === filter;
  });

  const handleApprove = async (requestId: string) => {
    setActionInProgress(requestId);
    try {
      await approveDeployment(requestId, null);
      // Remove from list after successful approval (real-time update will also fire)
      removeApproval(requestId);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setActionInProgress(null);
    }
  };

  const openRejectModal = (requestId: string, artifactName: string) => {
    setRejectModalState({
      isOpen: true,
      requestId,
      artifactName,
    });
  };

  const closeRejectModal = () => {
    setRejectModalState({
      isOpen: false,
      requestId: '',
      artifactName: '',
    });
  };

  const handleReject = async (reason: string) => {
    const { requestId } = rejectModalState;
    closeRejectModal();
    setActionInProgress(requestId);
    try {
      await rejectDeployment(requestId, reason);
      // Remove from list after successful rejection (real-time update will also fire)
      removeApproval(requestId);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setActionInProgress(null);
    }
  };

  // Format timestamp
  const formatTime = (timestamp: string | null): string => {
    if (timestamp === null) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  if (!isConnected) {
    return (
      <div style={pageStyle}>
        <h1 style={titleStyle}>Approvals</h1>
        <div style={loadingStyle}>Connecting to agent...</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={pageStyle}>
        <h1 style={titleStyle}>Approvals</h1>
        <div style={loadingStyle}>Loading approvals...</div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h1 style={titleStyle}>Approvals</h1>
        <div style={filterContainerStyle}>
          {filterOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setFilter(option.value)}
              style={filter === option.value ? filterButtonActiveStyle : filterButtonStyle}
              data-testid={`filter-${option.value}`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {error !== null && <div style={errorStyle}>Error: {error}</div>}

      {/* Approval Cards */}
      {approvals.length === 0 ? (
        <div style={emptyStateStyle}>No {filter === 'all' ? '' : filter} approvals.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing[4] }}>
          {approvals.map((approval) => (
            <div
              key={approval.id}
              style={cardStyle}
              data-testid="approval-card"
              data-request-id={approval.id}
              data-status={approval.status}
            >
              <div style={cardHeaderStyle}>
                <div>
                  <span style={cardTitleStyle}>{approval.artifact}</span>
                  <span style={cardVersionStyle}>{approval.version}</span>
                </div>
                <ApprovalStatusBadge status={approval.status} />
              </div>

              <div style={cardDetailsStyle}>
                <div style={detailItemStyle}>
                  <span style={detailLabelStyle}>Environment</span>
                  <span style={detailValueStyle}>{approval.environment}</span>
                </div>
                <div style={detailItemStyle}>
                  <span style={detailLabelStyle}>Requested By</span>
                  <span style={detailValueStyle}>{approval.requestedBy}</span>
                </div>
                <div style={detailItemStyle}>
                  <span style={detailLabelStyle}>Created</span>
                  <span style={detailValueStyle}>{formatTime(approval.createdAt)}</span>
                </div>
                {approval.expiresAt !== null && (
                  <div style={detailItemStyle}>
                    <span style={detailLabelStyle}>Expires</span>
                    <span style={detailValueStyle}>{formatTime(approval.expiresAt)}</span>
                  </div>
                )}
                {approval.stageName !== null && (
                  <div style={detailItemStyle}>
                    <span style={detailLabelStyle}>Stage</span>
                    <span style={detailValueStyle}>{approval.stageName}</span>
                  </div>
                )}
                {approval.isBreakGlass && (
                  <div style={detailItemStyle}>
                    <span style={detailLabelStyle}>Break Glass</span>
                    <span style={{ ...detailValueStyle, color: tokens.colors.warning }}>Yes</span>
                  </div>
                )}
              </div>

              {approval.reason !== null && (
                <div style={detailItemStyle}>
                  <span style={detailLabelStyle}>Reason</span>
                  <span style={detailValueStyle}>{approval.reason}</span>
                </div>
              )}

              {approval.reviewedBy !== null && (
                <div style={cardDetailsStyle}>
                  <div style={detailItemStyle}>
                    <span style={detailLabelStyle}>Reviewed By</span>
                    <span style={detailValueStyle}>{approval.reviewedBy}</span>
                  </div>
                  <div style={detailItemStyle}>
                    <span style={detailLabelStyle}>Reviewed At</span>
                    <span style={detailValueStyle}>{formatTime(approval.reviewedAt)}</span>
                  </div>
                  {approval.reviewComment !== null && (
                    <div style={detailItemStyle}>
                      <span style={detailLabelStyle}>Comment</span>
                      <span style={detailValueStyle}>{approval.reviewComment}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Actions - only for pending approvals */}
              {approval.status === 'pending' && (
                <div style={cardActionsStyle}>
                  <button
                    onClick={() => handleApprove(approval.id)}
                    disabled={actionInProgress !== null}
                    style={{
                      ...approveButtonStyle,
                      ...(actionInProgress !== null ? buttonDisabledStyle : {}),
                    }}
                    data-testid={`approve-${approval.id}`}
                  >
                    {actionInProgress === approval.id ? 'Processing...' : 'Approve'}
                  </button>
                  <button
                    onClick={() => openRejectModal(approval.id, approval.artifact)}
                    disabled={actionInProgress !== null}
                    style={{
                      ...rejectButtonStyle,
                      ...(actionInProgress !== null ? buttonDisabledStyle : {}),
                    }}
                    data-testid={`reject-${approval.id}`}
                  >
                    Reject
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Reject Modal */}
      <RejectModal
        isOpen={rejectModalState.isOpen}
        onClose={closeRejectModal}
        onConfirm={handleReject}
        artifactName={rejectModalState.artifactName}
      />
    </div>
  );
}
